import React, { useMemo } from "react";
import {
    BANK_RECONCILIATION_REPORT_LABELS,
    bankReconciliationReportDirectionalLabels,
} from "@common/constants/reports/bank-reconciliation-report";
import { buildAccountDisplayName } from "@common/helpers/financialAccount";
import { Card } from "@design-system/Card/Card";
import { CardSection } from "@design-system/Card/CardSection";
import { AccountingReportFooter } from "@components/accounting/AccountingReportFooter";
import { AccountingReportHeader } from "@components/accounting/AccountingReportHeader";
import {
    buildLocalReportChrome,
    useGeneratedReportFooterText,
} from "@components/accounting/localReportChrome";
import { filterTransactionGroup } from "./bankReconciliationReportSearch";
import { BankReconciliationReportSection } from "./BankReconciliationReportSection";
import { BankReconciliationReportSummary } from "./BankReconciliationReportSummary";
import { BankReconciliationReport } from "./types";
import styles from "./BankReconciliationReportContent.module.scss";
import reportStyles from "./BankReconciliationReport.module.scss";

interface Props {
    report: BankReconciliationReport;
    search: string;
    generationDate: Date;
}

export const BankReconciliationReportContent: React.FC<Props> = ({
    report,
    search,
    generationDate,
}) => {
    const footer = useGeneratedReportFooterText({
        generationDate,
        entityId: report.entityId,
        ledgerId: report.ledgerId ?? undefined,
    });
    const chrome = useMemo(
        () =>
            buildLocalReportChrome({
                title: report.entityName,
                subtitle: BANK_RECONCILIATION_REPORT_LABELS.reportType,
                subtitle2: buildAccountDisplayName(
                    report.accountName,
                    report.accountNumberMask,
                ),
                footer,
            }),
        [
            report.entityName,
            report.accountName,
            report.accountNumberMask,
            footer,
        ],
    );

    const clearedGroup = useMemo(
        () => filterTransactionGroup(report.cleared, search),
        [report.cleared, search],
    );
    const unclearedGroup = useMemo(
        () => filterTransactionGroup(report.uncleared, search),
        [report.uncleared, search],
    );
    const directionalLabels = bankReconciliationReportDirectionalLabels(
        report.isLiability ? "liability" : "bank",
    );

    return (
        <Card stacked sharpEdges fullPage>
            <CardSection className={styles.header}>
                <AccountingReportHeader chrome={chrome} />
            </CardSection>
            <div className={reportStyles.sections}>
                <BankReconciliationReportSummary
                    report={report}
                    clearedGroup={clearedGroup}
                    unclearedGroup={unclearedGroup}
                />
                <BankReconciliationReportSection
                    title={
                        BANK_RECONCILIATION_REPORT_LABELS.reconciledTransactions
                    }
                    group={clearedGroup}
                    depositsTitle={directionalLabels.depositAndCredits}
                    paymentsTitle={directionalLabels.checksAndPayments}
                    emptyMessage={
                        BANK_RECONCILIATION_REPORT_LABELS.noClearedTransactions
                    }
                    defaultOpen
                />
                <BankReconciliationReportSection
                    title={
                        BANK_RECONCILIATION_REPORT_LABELS.unreconciledTransactions
                    }
                    group={unclearedGroup}
                    depositsTitle={directionalLabels.depositAndCredits}
                    paymentsTitle={directionalLabels.checksAndPayments}
                    emptyMessage={
                        BANK_RECONCILIATION_REPORT_LABELS.noUnreconciledTransactions
                    }
                />
            </div>
            <AccountingReportFooter chrome={chrome} />
        </Card>
    );
};
